/**
 * G14 Price Import Page
 *
 * Allows users to upload CSV or paste JSON data with RCE (Rynkowa Cena Energii) prices from PSE API.
 * Data is sent to the importDynamicPriceData Lambda function via GraphQL mutation.
 *
 * Features:
 * - CSV upload with preview
 * - JSON paste from PSE API with preview
 * - Data validation and statistics
 * - Import progress and error handling
 */

import { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Paper,
  Tabs,
  Button,
  Group,
  Alert,
  Table,
  Textarea,
  TextInput,
  rem,
  Badge,
  SimpleGrid,
  Card,
  Divider,
  Progress,
} from '@mantine/core';
import { Dropzone, MIME_TYPES } from '@mantine/dropzone';
import {
  IconUpload,
  IconFileTypeCsv,
  IconX,
  IconAlertCircle,
  IconFileImport,
  IconCheck,
  IconCoin,
  IconCalendar,
  IconChartLine,
  IconArrowLeft,
  IconDownload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';
import { useAuth } from '../contexts/AuthContext';
import {
  useImportDynamicPriceData,
  type ImportDynamicPriceDataResult,
} from '../hooks/useImportDynamicPriceData';
import { useFetchPSEPriceData } from '../features/g14-calculator/hooks/useFetchPSEPriceData';

interface CSVRow {
  business_date: string;
  hour: number;
  rce_pln: number;
  pse_zone: string;
}

interface PSEAPIResponse {
  value: Array<{
    dtime: string;
    rce_pln: number;
  }>;
}

interface PreviewData {
  rows: CSVRow[];
  dateRange: {
    start: string;
    end: string;
  };
  hourCount: number;
  stats: {
    minPrice: number;
    maxPrice: number;
    avgPrice: number;
  };
  warnings: string[];
}

export function G14PriceImport() {
  useSEO({ title: 'Import danych cenowych G14', noindex: true });
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<string | null>('csv');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [jsonContent, setJsonContent] = useState<string>('');
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [importResult, setImportResult] = useState<ImportDynamicPriceDataResult | null>(null);

  // PSE API fetch state
  const currentYear = new Date().getFullYear();
  const [pseStartDate, setPseStartDate] = useState(`${currentYear}-01-01`);
  const [pseEndDate, setPseEndDate] = useState(
    `${currentYear}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`
  );

  const { importDynamicPriceData, loading, error, reset } = useImportDynamicPriceData();
  const {
    fetchPSEData,
    loading: pseLoading,
    error: pseError,
    result: pseResult,
    reset: pseReset,
  } = useFetchPSEPriceData();

  /**
   * Parse CSV content and generate preview
   */
  const parseCSVContent = (content: string): PreviewData | null => {
    try {
      const lines = content.trim().split('\n');
      if (lines.length < 2) {
        throw new Error('Plik CSV jest pusty lub zawiera tylko nagłówek');
      }

      // Skip header row
      const dataLines = lines.slice(1);
      const rows: CSVRow[] = [];
      const warnings: string[] = [];

      for (let i = 0; i < dataLines.length; i++) {
        const line = dataLines[i].trim();
        if (!line) continue;

        const parts = line.split(';');
        if (parts.length < 3) {
          warnings.push(`Wiersz ${i + 2}: nieprawidłowa liczba kolumn`);
          continue;
        }

        const [business_date, hourStr, rcePlnStr, pse_zone] = parts;
        const hour = parseInt(hourStr, 10);
        const rce_pln = parseFloat(rcePlnStr);

        if (isNaN(hour) || isNaN(rce_pln)) {
          warnings.push(`Wiersz ${i + 2}: nieprawidłowe dane liczbowe`);
          continue;
        }

        rows.push({
          business_date,
          hour,
          rce_pln,
          pse_zone: pse_zone || 'UNKNOWN',
        });
      }

      if (rows.length === 0) {
        throw new Error('Brak prawidłowych danych w pliku CSV');
      }

      // Calculate statistics
      const prices = rows.map(r => r.rce_pln / 1000); // Convert to PLN/kWh
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;

      // Determine date range
      const dates = rows.map(r => r.business_date).sort();
      const dateRange = {
        start: dates[0],
        end: dates[dates.length - 1],
      };

      return {
        rows: rows.slice(0, 10), // Preview first 10 rows
        dateRange,
        hourCount: rows.length,
        stats: {
          minPrice,
          maxPrice,
          avgPrice,
        },
        warnings,
      };
    } catch (err) {
      notifications.show({
        title: 'Błąd parsowania CSV',
        message: err instanceof Error ? err.message : 'Nie udało się przetworzyć pliku CSV',
        color: 'red',
      });
      return null;
    }
  };

  /**
   * Parse PSE API JSON and generate preview
   */
  const parseJSONContent = (content: string): PreviewData | null => {
    try {
      const data = JSON.parse(content) as PSEAPIResponse;

      if (!data.value || !Array.isArray(data.value)) {
        throw new Error('Nieprawidłowy format JSON - brak pola "value"');
      }

      if (data.value.length === 0) {
        throw new Error('Brak danych w odpowiedzi PSE API');
      }

      const rows: CSVRow[] = data.value.map(item => {
        const date = new Date(item.dtime);
        return {
          business_date: date.toISOString().split('T')[0],
          hour: date.getHours(),
          rce_pln: item.rce_pln,
          pse_zone: 'UNKNOWN', // PSE API doesn't provide zone in this format
        };
      });

      // Calculate statistics
      const prices = rows.map(r => r.rce_pln / 1000); // Convert to PLN/kWh
      const minPrice = Math.min(...prices);
      const maxPrice = Math.max(...prices);
      const avgPrice = prices.reduce((sum, p) => sum + p, 0) / prices.length;

      // Determine date range
      const dates = rows.map(r => r.business_date).sort();
      const dateRange = {
        start: dates[0],
        end: dates[dates.length - 1],
      };

      return {
        rows: rows.slice(0, 10), // Preview first 10 rows
        dateRange,
        hourCount: rows.length,
        stats: {
          minPrice,
          maxPrice,
          avgPrice,
        },
        warnings: [],
      };
    } catch (err) {
      notifications.show({
        title: 'Błąd parsowania JSON',
        message: err instanceof Error ? err.message : 'Nie udało się przetworzyć danych JSON',
        color: 'red',
      });
      return null;
    }
  };

  /**
   * Handle CSV file drop
   */
  const handleCSVDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) return;

    setCsvFile(file);
    setPreview(null);
    setImportResult(null);
    reset();

    try {
      const content = await file.text();
      setCsvContent(content);

      const previewData = parseCSVContent(content);
      if (previewData) {
        setPreview(previewData);
      }
    } catch (err) {
      notifications.show({
        title: 'Błąd odczytu pliku',
        message: 'Nie udało się odczytać pliku CSV',
        color: 'red',
      });
    }
  };

  /**
   * Handle JSON paste and preview
   */
  const handleJSONPreview = () => {
    if (!jsonContent.trim()) {
      notifications.show({
        title: 'Brak danych',
        message: 'Wklej dane JSON z PSE API',
        color: 'yellow',
      });
      return;
    }

    setPreview(null);
    setImportResult(null);
    reset();

    const previewData = parseJSONContent(jsonContent);
    if (previewData) {
      setPreview(previewData);
    }
  };

  /**
   * Handle import button click
   */
  const handleImport = async () => {
    if (!preview) return;

    try {
      const result = await importDynamicPriceData({
        csvContent: activeTab === 'csv' ? csvContent || undefined : undefined,
        jsonContent: activeTab === 'json' ? jsonContent || undefined : undefined,
      });

      setImportResult(result);

      notifications.show({
        title: 'Import zakończony pomyślnie',
        message: `Zaimportowano ${result.importedMonths.length} miesięcy z ${result.totalHours} godzinami danych cenowych`,
        color: 'green',
        icon: <IconCheck size={18} />,
        autoClose: 5000,
      });
    } catch (err) {
      // Error is already set in the hook
      if (import.meta.env.DEV) {
        console.error('[G14PriceImport] handleImport error:', err);
      }
    }
  };

  /**
   * Handle PSE API fetch
   */
  const handlePSEFetch = async () => {
    if (!pseStartDate || !pseEndDate) {
      notifications.show({
        title: 'Brak dat',
        message: 'Podaj zakres dat do pobrania',
        color: 'yellow',
      });
      return;
    }

    try {
      const result = await fetchPSEData(pseStartDate, pseEndDate);

      if (result.success) {
        notifications.show({
          title: 'Pobieranie zakończone',
          message: `Zaimportowano ${result.importedMonths.length} miesięcy (${result.totalHours} godzin)`,
          color: 'green',
          icon: <IconCheck size={18} />,
          autoClose: 5000,
        });
      }
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('[G14PriceImport] handlePSEFetch error:', err);
      }
    }
  };

  /**
   * Reset state for new import
   */
  const handleReset = () => {
    setCsvFile(null);
    setCsvContent(null);
    setJsonContent('');
    setPreview(null);
    setImportResult(null);
    reset();
  };

  return (
    <Layout isAuthenticated={isAuthenticated}>
      <Container size="lg" py="xl">
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Group mb="md">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={18} />}
                onClick={() => navigate('/kalkulatory/zaawansowany')}
              >
                Wróć do kalkulatora
              </Button>
            </Group>
            <Title order={1} mb="sm">
              Import danych cenowych G14
            </Title>
            <Text size="lg" c="dimmed">
              Prześlij dane RCE (Rynkowa Cena Energii) z PSE, aby wykorzystać taryfę dynamiczną G14 w kalkulatorze
            </Text>
          </div>

          {/* Info Alert */}
          <Alert icon={<IconAlertCircle size={20} />} color="blue" variant="light">
            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Czym jest taryfa G14?
              </Text>
              <Text size="sm">
                G14 to taryfa dynamiczna, gdzie cena energii zmienia się co godzinę na podstawie Rynkowej Ceny Energii (RCE) z Polskiej Sieci Elektroenergetycznej (PSE).
                Największe oszczędności uzyskasz przesuwając zużycie na godziny z najniższymi cenami (zwykle w nocy i weekendy).
              </Text>
            </Stack>
          </Alert>

          {/* Import Tabs */}
          {!importResult && (
            <Paper p="xl" shadow="sm" withBorder>
              <Tabs value={activeTab} onChange={setActiveTab}>
                <Tabs.List>
                  <Tabs.Tab value="csv" leftSection={<IconFileTypeCsv size={16} />}>
                    Import CSV
                  </Tabs.Tab>
                  <Tabs.Tab value="json" leftSection={<IconFileImport size={16} />}>
                    Wklej JSON z PSE API
                  </Tabs.Tab>
                  <Tabs.Tab value="pse" leftSection={<IconDownload size={16} />}>
                    Pobierz z PSE API
                  </Tabs.Tab>
                </Tabs.List>

                {/* CSV Upload Tab */}
                <Tabs.Panel value="csv" pt="xl">
                  <Stack gap="lg">
                    <div>
                      <Text size="sm" mb="md" c="dimmed">
                        Prześlij plik CSV z danymi RCE. Oczekiwany format:
                      </Text>
                      <Paper p="sm" withBorder bg="gray.0" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        business_date;hour;rce_pln;pse_zone
                        <br />
                        2025-01-15;0;425.50;GREEN
                        <br />
                        2025-01-15;1;410.20;GREEN
                      </Paper>
                    </div>

                    <Dropzone
                      onDrop={handleCSVDrop}
                      maxSize={2 * 1024 * 1024}
                      accept={[MIME_TYPES.csv, 'text/csv', 'application/csv']}
                      disabled={loading}
                      multiple={false}
                    >
                      <Group
                        justify="center"
                        gap="xl"
                        mih={120}
                        style={{ pointerEvents: 'none' }}
                      >
                        <Dropzone.Accept>
                          <IconUpload
                            style={{
                              width: rem(52),
                              height: rem(52),
                              color: 'var(--mantine-color-blue-6)',
                            }}
                            stroke={1.5}
                          />
                        </Dropzone.Accept>
                        <Dropzone.Reject>
                          <IconX
                            style={{
                              width: rem(52),
                              height: rem(52),
                              color: 'var(--mantine-color-red-6)',
                            }}
                            stroke={1.5}
                          />
                        </Dropzone.Reject>
                        <Dropzone.Idle>
                          <IconFileTypeCsv
                            style={{
                              width: rem(52),
                              height: rem(52),
                              color: 'var(--mantine-color-dimmed)',
                            }}
                            stroke={1.5}
                          />
                        </Dropzone.Idle>

                        <div>
                          <Text size="lg" fw={500} inline>
                            Przeciągnij plik CSV tutaj lub kliknij, aby wybrać
                          </Text>
                          <Text size="sm" c="dimmed" inline mt={7}>
                            Maksymalny rozmiar: 2 MB (do 4 lat danych)
                          </Text>
                        </div>
                      </Group>
                    </Dropzone>

                    {csvFile && (
                      <Alert
                        color="blue"
                        variant="light"
                        icon={<IconFileTypeCsv size={18} />}
                        title="Wybrany plik"
                      >
                        <Group justify="space-between">
                          <div>
                            <Text size="sm" fw={500}>
                              {csvFile.name}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Rozmiar: {(csvFile.size / 1024).toFixed(2)} KB
                            </Text>
                          </div>
                          {!loading && (
                            <Button variant="subtle" size="xs" onClick={() => {
                              setCsvFile(null);
                              setCsvContent(null);
                              setPreview(null);
                            }}>
                              Usuń
                            </Button>
                          )}
                        </Group>
                      </Alert>
                    )}
                  </Stack>
                </Tabs.Panel>

                {/* JSON Paste Tab */}
                <Tabs.Panel value="json" pt="xl">
                  <Stack gap="lg">
                    <div>
                      <Text size="sm" mb="md" c="dimmed">
                        Wklej odpowiedź JSON z PSE API. Oczekiwany format:
                      </Text>
                      <Paper p="sm" withBorder bg="gray.0" style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {`{ "value": [{ "dtime": "2025-01-15T00:00:00", "rce_pln": 425.50 }] }`}
                      </Paper>
                    </div>

                    <Textarea
                      label="Dane JSON z PSE API"
                      placeholder='{ "value": [{ "dtime": "2025-01-15T00:00:00", "rce_pln": 425.50 }] }'
                      value={jsonContent}
                      onChange={(e) => setJsonContent(e.currentTarget.value)}
                      minRows={10}
                      maxRows={20}
                      disabled={loading}
                      autosize
                    />

                    <Group justify="flex-end">
                      <Button
                        variant="light"
                        onClick={handleJSONPreview}
                        disabled={!jsonContent.trim() || loading}
                      >
                        Podgląd danych
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>

                {/* PSE API Fetch Tab */}
                <Tabs.Panel value="pse" pt="xl">
                  <Stack gap="lg">
                    <Alert icon={<IconAlertCircle size={18} />} color="blue" variant="light">
                      <Text size="sm">
                        Dane pobierane automatycznie z PSE RCE API (darmowe, publiczne).
                        Ceny PLN/MWh z 15-minutowymi interwałami, agregowane do godzinowych.
                        Strefy cenowe przypisywane automatycznie.
                      </Text>
                    </Alert>

                    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                      <TextInput
                        label="Data początkowa"
                        type="date"
                        value={pseStartDate}
                        onChange={(e) => setPseStartDate(e.currentTarget.value)}
                        disabled={pseLoading}
                      />
                      <TextInput
                        label="Data końcowa"
                        type="date"
                        value={pseEndDate}
                        onChange={(e) => setPseEndDate(e.currentTarget.value)}
                        disabled={pseLoading}
                      />
                    </SimpleGrid>

                    {pseLoading && (
                      <Stack gap="xs">
                        <Text size="sm" c="dimmed">
                          Pobieranie danych z PSE API... (może potrwać do 2 minut)
                        </Text>
                        <Progress value={100} animated color="blue" />
                      </Stack>
                    )}

                    {pseError && (
                      <Alert
                        color="red"
                        icon={<IconAlertCircle size={18} />}
                        title="Błąd pobierania"
                        withCloseButton
                        onClose={pseReset}
                      >
                        <Text size="sm">{pseError}</Text>
                      </Alert>
                    )}

                    {/* PSE Fetch Result */}
                    {pseResult && pseResult.success && (
                      <Paper p="md" withBorder bg="green.0">
                        <Stack gap="md">
                          <Group>
                            <IconCheck size={24} color="var(--mantine-color-green-6)" />
                            <Text fw={600} c="green">Import zakończony pomyślnie</Text>
                          </Group>

                          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                            <Card withBorder>
                              <Stack gap="xs">
                                <Text size="sm" c="dimmed">Zaimportowane miesiące</Text>
                                <Text size="xl" fw={700}>{pseResult.importedMonths.length}</Text>
                                <Text size="xs" c="dimmed">
                                  {pseResult.importedMonths.join(', ')}
                                </Text>
                              </Stack>
                            </Card>
                            <Card withBorder>
                              <Stack gap="xs">
                                <Text size="sm" c="dimmed">Łączna liczba godzin</Text>
                                <Text size="xl" fw={700}>
                                  {pseResult.totalHours.toLocaleString('pl-PL')}
                                </Text>
                              </Stack>
                            </Card>
                            <Card withBorder>
                              <Stack gap="xs">
                                <Text size="sm" c="dimmed">Pominięte dni</Text>
                                <Text size="xl" fw={700}>{pseResult.skippedDates.length}</Text>
                                {pseResult.skippedDates.length > 0 && (
                                  <Text size="xs" c="dimmed">
                                    {pseResult.skippedDates.slice(0, 5).join(', ')}
                                    {pseResult.skippedDates.length > 5 && ` +${pseResult.skippedDates.length - 5}`}
                                  </Text>
                                )}
                              </Stack>
                            </Card>
                          </SimpleGrid>

                          {pseResult.errors && pseResult.errors.length > 0 && (
                            <Alert color="yellow" icon={<IconAlertCircle size={18} />} title="Ostrzeżenia">
                              <Stack gap="xs">
                                {pseResult.errors.slice(0, 5).map((err, idx) => (
                                  <Text key={idx} size="sm">{err}</Text>
                                ))}
                              </Stack>
                            </Alert>
                          )}
                        </Stack>
                      </Paper>
                    )}

                    {pseResult && !pseResult.success && (
                      <Alert color="red" icon={<IconAlertCircle size={18} />} title="Import nieudany">
                        <Text size="sm">{pseResult.message}</Text>
                      </Alert>
                    )}

                    <Group justify="flex-end">
                      {pseResult && (
                        <Button variant="subtle" onClick={pseReset}>
                          Resetuj
                        </Button>
                      )}
                      <Button
                        size="lg"
                        leftSection={<IconDownload size={20} />}
                        onClick={handlePSEFetch}
                        loading={pseLoading}
                        disabled={!pseStartDate || !pseEndDate}
                      >
                        Pobierz dane z PSE
                      </Button>
                    </Group>
                  </Stack>
                </Tabs.Panel>
              </Tabs>
            </Paper>
          )}

          {/* Error Display */}
          {error && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={18} />}
              title="Błąd importu"
              withCloseButton
              onClose={reset}
            >
              <Text size="sm">{error}</Text>
            </Alert>
          )}

          {/* Preview Section */}
          {preview && !importResult && (
            <Paper p="xl" shadow="sm" withBorder>
              <Stack gap="lg">
                <Title order={2} size="h3">
                  Podgląd danych
                </Title>

                {/* Statistics */}
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  <Card withBorder>
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconCalendar size={20} color="var(--mantine-color-blue-6)" />
                        <Text size="sm" c="dimmed">
                          Zakres dat
                        </Text>
                      </Group>
                      <Text size="lg" fw={600}>
                        {preview.dateRange.start} - {preview.dateRange.end}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder>
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconChartLine size={20} color="var(--mantine-color-green-6)" />
                        <Text size="sm" c="dimmed">
                          Liczba godzin
                        </Text>
                      </Group>
                      <Text size="lg" fw={600}>
                        {preview.hourCount}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder>
                    <Stack gap="xs">
                      <Group gap="xs">
                        <IconCoin size={20} color="var(--mantine-color-yellow-6)" />
                        <Text size="sm" c="dimmed">
                          Średnia cena
                        </Text>
                      </Group>
                      <Text size="lg" fw={600}>
                        {preview.stats.avgPrice.toFixed(4)} zł/kWh
                      </Text>
                    </Stack>
                  </Card>
                </SimpleGrid>

                {/* Price Statistics */}
                <Paper withBorder p="md" bg="gray.0">
                  <Text size="sm" fw={500} mb="xs">
                    Statystyki cen (PLN/kWh):
                  </Text>
                  <SimpleGrid cols={{ base: 1, sm: 3 }}>
                    <div>
                      <Text size="xs" c="dimmed">Minimalna</Text>
                      <Text size="md" fw={600} c="green">
                        {preview.stats.minPrice.toFixed(4)} zł
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Maksymalna</Text>
                      <Text size="md" fw={600} c="red">
                        {preview.stats.maxPrice.toFixed(4)} zł
                      </Text>
                    </div>
                    <div>
                      <Text size="xs" c="dimmed">Średnia</Text>
                      <Text size="md" fw={600} c="blue">
                        {preview.stats.avgPrice.toFixed(4)} zł
                      </Text>
                    </div>
                  </SimpleGrid>
                </Paper>

                {/* Warnings */}
                {preview.warnings.length > 0 && (
                  <Alert color="yellow" icon={<IconAlertCircle size={18} />} title="Ostrzeżenia">
                    <Stack gap="xs">
                      {preview.warnings.slice(0, 5).map((warning, idx) => (
                        <Text key={idx} size="sm">
                          {warning}
                        </Text>
                      ))}
                      {preview.warnings.length > 5 && (
                        <Text size="xs" c="dimmed">
                          ... i {preview.warnings.length - 5} więcej
                        </Text>
                      )}
                    </Stack>
                  </Alert>
                )}

                {/* Preview Table */}
                <div>
                  <Text size="sm" fw={500} mb="md">
                    Pierwsze 10 wierszy:
                  </Text>
                  <Table striped highlightOnHover withTableBorder>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Data</Table.Th>
                        <Table.Th>Godzina</Table.Th>
                        <Table.Th>Cena RCE (PLN/MWh)</Table.Th>
                        <Table.Th>Cena RCE (PLN/kWh)</Table.Th>
                        <Table.Th>Strefa PSE</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {preview.rows.map((row, idx) => (
                        <Table.Tr key={idx}>
                          <Table.Td>{row.business_date}</Table.Td>
                          <Table.Td>{row.hour}:00</Table.Td>
                          <Table.Td>{row.rce_pln.toFixed(2)}</Table.Td>
                          <Table.Td>{(row.rce_pln / 1000).toFixed(4)}</Table.Td>
                          <Table.Td>
                            <Badge size="sm" color={
                              row.pse_zone === 'GREEN' ? 'green' :
                              row.pse_zone === 'LIGHT_GREEN' ? 'lime' :
                              row.pse_zone === 'YELLOW' ? 'yellow' :
                              row.pse_zone === 'RED' ? 'red' :
                              'gray'
                            }>
                              {row.pse_zone}
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>

                {/* Import Button */}
                <Group justify="space-between">
                  <Button variant="subtle" onClick={handleReset} disabled={loading}>
                    Anuluj
                  </Button>
                  <Button
                    size="lg"
                    leftSection={<IconUpload size={20} />}
                    onClick={handleImport}
                    loading={loading}
                  >
                    Importuj dane
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Import Success */}
          {importResult && importResult.success && (
            <Paper p="xl" shadow="sm" withBorder bg="green.0">
              <Stack gap="lg">
                <Group>
                  <IconCheck size={32} color="var(--mantine-color-green-6)" />
                  <div>
                    <Title order={2} size="h3" c="green">
                      Import zakończony pomyślnie!
                    </Title>
                    <Text size="sm" c="dimmed">
                      Dane cenowe RCE zostały zapisane w systemie
                    </Text>
                  </div>
                </Group>

                <Divider />

                {/* Import Summary */}
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                  <Card withBorder>
                    <Stack gap="xs">
                      <Text size="sm" c="dimmed">
                        Zaimportowane miesiące
                      </Text>
                      <Text size="xl" fw={700}>
                        {importResult.importedMonths.length}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {importResult.importedMonths.join(', ')}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder>
                    <Stack gap="xs">
                      <Text size="sm" c="dimmed">
                        Liczba godzin
                      </Text>
                      <Text size="xl" fw={700}>
                        {importResult.totalHours}
                      </Text>
                    </Stack>
                  </Card>

                  <Card withBorder>
                    <Stack gap="xs">
                      <Text size="sm" c="dimmed">
                        Średnia cena
                      </Text>
                      <Text size="xl" fw={700}>
                        {importResult.stats?.avgPriceKwh.toFixed(4)} zł/kWh
                      </Text>
                      <Text size="xs" c="dimmed">
                        {importResult.stats?.avgPriceMwh.toFixed(2)} zł/MWh
                      </Text>
                    </Stack>
                  </Card>
                </SimpleGrid>

                {/* Price Range */}
                {importResult.stats && (
                  <Paper withBorder p="md" style={{ backgroundColor: 'white' }}>
                    <Text size="sm" fw={500} mb="md">
                      Zakres cen (PLN/kWh):
                    </Text>
                    <Group justify="space-between" grow>
                      <div>
                        <Text size="xs" c="dimmed">Minimalna</Text>
                        <Text size="lg" fw={600} c="green">
                          {importResult.stats.minPriceKwh.toFixed(4)} zł
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Maksymalna</Text>
                        <Text size="lg" fw={600} c="red">
                          {importResult.stats.maxPriceKwh.toFixed(4)} zł
                        </Text>
                      </div>
                      <div>
                        <Text size="xs" c="dimmed">Średnia</Text>
                        <Text size="lg" fw={600} c="blue">
                          {importResult.stats.avgPriceKwh.toFixed(4)} zł
                        </Text>
                      </div>
                    </Group>
                  </Paper>
                )}

                {/* Errors (if any) */}
                {importResult.errors && importResult.errors.length > 0 && (
                  <Alert color="yellow" icon={<IconAlertCircle size={18} />} title="Ostrzeżenia">
                    <Stack gap="xs">
                      {importResult.errors.map((err, idx) => (
                        <Text key={idx} size="sm">
                          {err}
                        </Text>
                      ))}
                    </Stack>
                  </Alert>
                )}

                {/* Action Buttons */}
                <Group justify="space-between">
                  <Button
                    variant="light"
                    onClick={handleReset}
                  >
                    Importuj więcej danych
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => navigate('/kalkulatory/zaawansowany')}
                  >
                    Wróć do kalkulatora PRO
                  </Button>
                </Group>
              </Stack>
            </Paper>
          )}

          {/* Instructions */}
          {!importResult && (
            <Paper p="md" withBorder radius="md">
              <Stack gap="xs">
                <Text fw={600}>Jak pobrać dane RCE z PSE?</Text>
                <ol style={{ marginLeft: '1.5rem' }}>
                  <li>
                    <Text size="sm">
                      Przejdź na{' '}
                      <a
                        href="https://www.pse.pl/dane-systemowe/funkcjonowanie-rb/raporty-dobowe-z-funkcjonowania-rb/podstawowe-wskazniki-cenowe-i-kosztowe"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        portal PSE - RCE
                      </a>
                    </Text>
                  </li>
                  <li>
                    <Text size="sm">Wybierz zakres dat (np. cały rok 2025)</Text>
                  </li>
                  <li>
                    <Text size="sm">Pobierz dane w formacie CSV lub skopiuj JSON z API</Text>
                  </li>
                  <li>
                    <Text size="sm">Prześlij dane za pomocą formularza powyżej</Text>
                  </li>
                  <li>
                    <Text size="sm">System automatycznie przetworzy dane i udostępni taryfę G14 w kalkulatorze</Text>
                  </li>
                </ol>
              </Stack>
            </Paper>
          )}
        </Stack>
      </Container>
    </Layout>
  );
}
